import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useHistory, useParams } from "react-router-dom";
import { withStyles } from "@mui/styles";

import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import Checkbox from "@mui/material/Checkbox";

import ReportDetailModal from "./ReportDetailModal";
import BulkActionToolbar from "./BulkActionToolbar";
import utils from "../../universal/utils";

const basePath = utils.getEdgeBasePath();

const styles = (theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    paddingTop: "5rem",
    backgroundColor: "#0b0b0b",
  },
  container: {
    width: "90%",
    maxWidth: "1600px",
    margin: "2rem auto",
  },
  header: {
    display: "flex",
    alignItems: "center",
    marginBottom: "2rem",
    gap: "1rem",
    flexWrap: "wrap",
  },
  title: {
    fontSize: "2rem",
    fontWeight: 700,
    color: "#ffffff",
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    [theme.breakpoints.up("xl")]: {
      fontSize: "2.5rem",
    },
  },
  titleIcon: {
    fontSize: "2rem",
    color: "#E8A33D",
  },
  statsContainer: {
    display: "flex",
    gap: "1rem",
    marginBottom: "2rem",
    flexWrap: "wrap",
  },
  statCard: {
    backgroundColor: "#1C1E20",
    borderRadius: "12px",
    padding: "1.5rem",
    border: "1px solid rgba(232, 163, 61, 0.2)",
    flex: "1",
    minWidth: "200px",
  },
  statValue: {
    fontSize: "2rem",
    fontWeight: 700,
    color: "#E8A33D",
  },
  statLabel: {
    fontSize: "0.9rem",
    color: "rgba(255,255,255,0.7)",
    marginTop: "0.5rem",
  },
  filtersContainer: {
    backgroundColor: "#1C1E20",
    borderRadius: "12px",
    padding: "1.5rem",
    marginBottom: "2rem",
    border: "1px solid rgba(232, 163, 61, 0.2)",
  },
  filtersRow: {
    display: "flex",
    gap: "1rem",
    flexWrap: "wrap",
    alignItems: "flex-end",
  },
  filterControl: {
    minWidth: "180px",
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#0b0b0b",
      "& fieldset": {
        borderColor: "rgba(232, 163, 61, 0.3)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(232, 163, 61, 0.5)",
      },
    },
  },
  tableContainer: {
    backgroundColor: "#1C1E20",
    border: "1px solid rgba(232, 163, 61, 0.2)",
    borderRadius: "12px",
  },
  tableHeader: {
    backgroundColor: "rgba(232, 163, 61, 0.1)",
    "& th": {
      color: "#ffffff",
      fontWeight: 700,
      borderBottom: "2px solid rgba(232, 163, 61, 0.3)",
    },
  },
  tableRow: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.03)",
    },
    "& td": {
      borderBottom: "1px solid rgba(255,255,255,0.1)",
      color: "rgba(255,255,255,0.9)",
    },
  },
  statusChip: {
    fontSize: "0.75rem",
    height: "24px",
    fontWeight: 700,
  },
  actionButton: {
    minWidth: "40px",
    padding: "0.25rem 0.5rem",
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "4rem",
  },
  emptyState: {
    textAlign: "center",
    padding: "4rem 2rem",
    color: "rgba(255,255,255,0.6)",
  },
});

const offenseTypeLabels = {
  cheating: "Cheating",
  griefing: "Griefing",
  smurfing: "Smurfing",
  abusive_comms: "Abusive Comms",
  not_team_player: "Not Team Player",
  non_communicative: "Non Communicative",
};

const statusColors = {
  PENDING: "#FFC107",
  VALIDATED: "#00C853",
  DISMISSED: "#9E9E9E",
  FALSE_REPORT: "#F44336",
};

function ReputationReviewPage({ classes, catalog }) {
  const history = useHistory();
  const { userId } = useParams();
  const { user } = catalog;

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalReports, setTotalReports] = useState(0);
  const [selectedReports, setSelectedReports] = useState([]);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState("PENDING");
  const [offenseFilter, setOffenseFilter] = useState("all");
  const [timeFilter, setTimeFilter] = useState("all");
  const [credibilityFilter, setCredibilityFilter] = useState("all");

  // Stats
  const [stats, setStats] = useState({
    pending: 0,
    validatedToday: 0,
    dismissedToday: 0,
  });

  useEffect(() => {
    if (userId) {
      fetchReports();
      fetchStats();
    }
  }, [userId, page, rowsPerPage, statusFilter, offenseFilter, timeFilter, credibilityFilter]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        status: statusFilter,
        limit: rowsPerPage.toString(),
        offset: (page * rowsPerPage).toString(),
      });

      if (offenseFilter !== "all") params.append("offenseType", offenseFilter);
      if (timeFilter !== "all") params.append("timeRange", timeFilter);
      if (credibilityFilter !== "all") params.append("credibility", credibilityFilter);

      const response = await fetch(
        `${basePath}/api/v1/admin/reputation/pending-reports/adpass/${userId}?${params}`,
        {
          credentials: "include",
        }
      );

      const result = await response.json();
      if (result.ok) {
        setReports(result.reports || []);
        setTotalReports(result.total || 0);
      }
      setLoading(false);
    } catch (err) {
      console.error("Error fetching reports:", err);
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(
        `${basePath}/api/v1/admin/reputation/platform-stats/adpass/${userId}`,
        {
          credentials: "include",
        }
      );

      const result = await response.json();
      if (result.ok) {
        setStats(result.stats || {});
      }
    } catch (err) {
      console.error("Error fetching stats:", err);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedReports(reports.map((r) => r._id));
    } else {
      setSelectedReports([]);
    }
  };

  const handleSelectReport = (reportId) => {
    setSelectedReports((prev) => {
      if (prev.includes(reportId)) {
        return prev.filter((id) => id !== reportId);
      } else {
        return [...prev, reportId];
      }
    });
  };

  const handleRowClick = (report) => {
    setSelectedReport(report);
    setDetailModalOpen(true);
  };

  const handleCloseModal = () => {
    setDetailModalOpen(false);
    setSelectedReport(null);
  };

  const handleReviewComplete = () => {
    setDetailModalOpen(false);
    setSelectedReport(null);
    setSelectedReports([]);
    fetchReports();
    fetchStats();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (!userId) {
    return (
      <div className={classes.root}>
        <div className={classes.container}>
          <Paper style={{ padding: "2rem", textAlign: "center" }}>
            <Typography variant="h6" style={{ color: "#F44336" }}>
              Admin access required
            </Typography>
          </Paper>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.container}>
        {/* Header */}
        <div className={classes.header}>
          <Typography className={classes.title}>
            <SearchIcon className={classes.titleIcon} />
            Reputation Report Review
          </Typography>
          <Chip
            label={`Admin: ${userId}`}
            style={{ backgroundColor: "#E8A33D", color: "#fff", fontWeight: 700 }}
          />
        </div>

        {/* Stats Cards */}
        <div className={classes.statsContainer}>
          <Paper className={classes.statCard}>
            <Typography className={classes.statValue}>{stats.pending || 0}</Typography>
            <Typography className={classes.statLabel}>Pending Reports</Typography>
          </Paper>
          <Paper className={classes.statCard}>
            <Typography className={classes.statValue}>{stats.validatedToday || 0}</Typography>
            <Typography className={classes.statLabel}>Validated Today</Typography>
          </Paper>
          <Paper className={classes.statCard}>
            <Typography className={classes.statValue}>{stats.dismissedToday || 0}</Typography>
            <Typography className={classes.statLabel}>Dismissed Today</Typography>
          </Paper>
        </div>

        {/* Filters */}
        <Paper className={classes.filtersContainer}>
          <Typography style={{ fontWeight: 700, color: "#fff", marginBottom: "1rem" }}>
            Filters
          </Typography>
          <div className={classes.filtersRow}>
            <FormControl className={classes.filterControl}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="VALIDATED">Validated</MenuItem>
                <MenuItem value="DISMISSED">Dismissed</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </Select>
            </FormControl>

            <FormControl className={classes.filterControl}>
              <InputLabel>Offense Type</InputLabel>
              <Select value={offenseFilter} label="Offense Type" onChange={(e) => setOffenseFilter(e.target.value)}>
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="cheating">Cheating</MenuItem>
                <MenuItem value="griefing">Griefing</MenuItem>
                <MenuItem value="smurfing">Smurfing</MenuItem>
                <MenuItem value="abusive_comms">Abusive Comms</MenuItem>
                <MenuItem value="not_team_player">Not Team Player</MenuItem>
                <MenuItem value="non_communicative">Non Communicative</MenuItem>
              </Select>
            </FormControl>

            <FormControl className={classes.filterControl}>
              <InputLabel>Time Range</InputLabel>
              <Select value={timeFilter} label="Time Range" onChange={(e) => setTimeFilter(e.target.value)}>
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="24h">Last 24 Hours</MenuItem>
                <MenuItem value="7d">Last 7 Days</MenuItem>
                <MenuItem value="30d">Last 30 Days</MenuItem>
              </Select>
            </FormControl>

            <FormControl className={classes.filterControl}>
              <InputLabel>Reporter Credibility</InputLabel>
              <Select value={credibilityFilter} label="Reporter Credibility" onChange={(e) => setCredibilityFilter(e.target.value)}>
                <MenuItem value="all">All Levels</MenuItem>
                <MenuItem value="high">High (&gt;80)</MenuItem>
                <MenuItem value="medium">Medium (50-80)</MenuItem>
                <MenuItem value="low">Low (&lt;50)</MenuItem>
              </Select>
            </FormControl>
          </div>
        </Paper>

        {/* Bulk Actions Toolbar */}
        {selectedReports.length > 0 && (
          <BulkActionToolbar
            selectedCount={selectedReports.length}
            selectedReports={selectedReports}
            userId={userId}
            onComplete={handleReviewComplete}
          />
        )}

        {/* Reports Table */}
        {loading ? (
          <div className={classes.loading}>
            <CircularProgress size={60} style={{ color: "#E8A33D" }} />
          </div>
        ) : (
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table>
              <TableHead className={classes.tableHeader}>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedReports.length > 0 && selectedReports.length < reports.length}
                      checked={reports.length > 0 && selectedReports.length === reports.length}
                      onChange={handleSelectAll}
                      style={{ color: "#E8A33D" }}
                    />
                  </TableCell>
                  <TableCell>Report ID</TableCell>
                  <TableCell>Reported Player</TableCell>
                  <TableCell>Reporter</TableCell>
                  <TableCell>Offense Type</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reports.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <div className={classes.emptyState}>
                        <Typography>No reports found</Typography>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  reports.map((report) => (
                    <TableRow
                      key={report._id}
                      className={classes.tableRow}
                      onClick={() => handleRowClick(report)}
                    >
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedReports.includes(report._id)}
                          onChange={() => handleSelectReport(report._id)}
                          style={{ color: "#E8A33D" }}
                        />
                      </TableCell>
                      <TableCell>{report._id.substring(0, 8)}...</TableCell>
                      <TableCell>{report.reportedSteamId || "N/A"}</TableCell>
                      <TableCell>{report.reporterSteamId || "N/A"}</TableCell>
                      <TableCell>
                        <Chip
                          label={offenseTypeLabels[report.offenseType] || report.offenseType}
                          size="small"
                          style={{ backgroundColor: "rgba(232, 163, 61, 0.2)", color: "#E8A33D" }}
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(report.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={report.reportStatus || "PENDING"}
                          className={classes.statusChip}
                          style={{
                            backgroundColor: statusColors[report.reportStatus] || statusColors.PENDING,
                            color: "#fff",
                          }}
                        />
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="contained"
                          size="small"
                          className={classes.actionButton}
                          style={{ backgroundColor: "#00C853", marginRight: "0.5rem" }}
                          startIcon={<CheckCircleIcon />}
                        >
                          Review
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalReports}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              style={{ color: "rgba(255,255,255,0.9)" }}
            />
          </TableContainer>
        )}

        {/* Detail Modal */}
        {detailModalOpen && selectedReport && (
          <ReportDetailModal
            open={detailModalOpen}
            report={selectedReport}
            userId={userId}
            onClose={handleCloseModal}
            onComplete={handleReviewComplete}
          />
        )}
      </div>
    </div>
  );
}

ReputationReviewPage.propTypes = {
  classes: PropTypes.object.isRequired,
  catalog: PropTypes.object.isRequired,
};

export default withStyles(styles)(ReputationReviewPage);
